// تب سقف‌های تأیید SCR-STAFF-922 (US-IAM-034، BR-IAM-036): ماتریس نقش × کلید سقف (درصد یا مبلغ ریالی؛ خالی ← نامحدود)، نشان
// «پیش‌فرض» برای مقدار قالب و ویرایش به ازای نقش (PUT approval-limits/{roleId})؛ سقف مالک قفل و نامحدود؛ سقف بیش از سقف ویرایشگر
// رد می‌شود (PHX-IAM-0505 — C-AGENT-384). سقف مؤثر عضو = بیشینه نقش‌های او.
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ApprovalLimitDefinition,
  type ApprovalLimitItem,
  type ApprovalLimitRoleRow,
  type ApprovalLimitUnit,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, keepDigitsOnly, Money, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { iamKeys, useRoleLabel } from './common';

/** نمایش مقدار سقف: نامحدود، درصد یا مبلغ (Money با واحد نمایش مستأجر). */
export function ApprovalLimitValue({
  unit,
  value,
}: {
  unit: ApprovalLimitUnit;
  value: number | string | null | undefined;
}) {
  const { t } = useTranslation('iam');
  if (value === null || value === undefined) {
    return <>{t('iam:approval_limits.unlimited')}</>;
  }
  if (unit === 'Percent') {
    return (
      <bdi>
        {faDigits(value)}
        {t('iam:approval_limits.percent_suffix')}
      </bdi>
    );
  }
  return <Money amountMinor={value} />;
}

function LimitsDialog({
  row,
  keys,
  onClose,
  onDone,
}: {
  row: ApprovalLimitRoleRow;
  keys: readonly ApprovalLimitDefinition[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const roleLabel = useRoleLabel();
  const initial = Object.fromEntries(
    row.limits.map((item) => [item.key, item.value === null ? null : String(item.value)]),
  ) as Record<string, string | null>;
  const [values, setValues] = useState<Record<string, string | null>>(initial);
  const [dirty, setDirty] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/identity/approval-limits/{roleId}', {
          params: { path: { roleId: row.roleId } },
          body: {
            limits: keys.map((definition) => {
              const value = values[definition.key];
              return { key: definition.key, value: value === null || value === undefined ? null : Number(value || 0) };
            }),
          },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: iamKeys.approvalLimits(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: iamKeys.myApprovalLimits(session?.tenantId) });
      onDone();
    },
  });

  return (
    <FormDialog
      open
      titleKey="iam:approval_limits.edit_title"
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
      onClose={onClose}
      onSubmit={() => save.mutate()}
    >
      <Typography color="text.secondary">{roleLabel(row.code)}</Typography>
      {keys.map((definition) => {
        const value = values[definition.key];
        const unlimited = value === null || value === undefined;
        const label = t(`iam:approval_limits.keys.${definition.key}`);
        return (
          <Stack key={definition.key} spacing={0.5} data-limit-key={definition.key}>
            <TextField
              label={label}
              value={unlimited ? '' : faDigits(value)}
              disabled={unlimited}
              onChange={(event) => {
                const digits = keepDigitsOnly(event.target.value).slice(0, definition.unit === 'Percent' ? 3 : 15);
                const bounded =
                  definition.unit === 'Percent' && digits !== '' ? String(Math.min(100, Number(digits))) : digits;
                setValues((current) => ({ ...current, [definition.key]: bounded }));
                setDirty(true);
              }}
              slotProps={{
                htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': `limit-input-${definition.key}` },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {definition.unit === 'Percent'
                        ? t('iam:approval_limits.percent_suffix')
                        : t('iam:approval_limits.rial_suffix')}
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={unlimited}
                  onChange={(event) => {
                    setValues((current) => ({ ...current, [definition.key]: event.target.checked ? null : '0' }));
                    setDirty(true);
                  }}
                  slotProps={{ input: { 'aria-label': `${label} — ${t('iam:approval_limits.unlimited_label')}` } }}
                />
              }
              label={t('iam:approval_limits.unlimited_label')}
            />
          </Stack>
        );
      })}
    </FormDialog>
  );
}

export function ApprovalLimitsTab({ onNotice }: { onNotice: (message: string) => void }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const manage = useWriteAccess('iam.role.manage');
  const roleLabel = useRoleLabel();
  const [editing, setEditing] = useState<ApprovalLimitRoleRow | null>(null);
  const matrix = useQuery({
    queryKey: iamKeys.approvalLimits(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/approval-limits')),
  });
  const keys = matrix.data?.keys ?? [];

  const columns: DataTableColumn<ApprovalLimitRoleRow>[] = [
    {
      id: 'role',
      headerKey: 'iam:approval_limits.role',
      cell: (row) => <Typography sx={{ fontWeight: 600 }}>{roleLabel(row.code)}</Typography>,
    },
    ...keys.map<DataTableColumn<ApprovalLimitRoleRow>>((definition) => ({
      id: definition.key,
      headerKey: `iam:approval_limits.keys.${definition.key}`,
      cell: (row) => {
        const item: ApprovalLimitItem | undefined = row.limits.find((limit) => limit.key === definition.key);
        return (
          <Box
            component="span"
            data-testid={`limit-${row.code}-${definition.key}`}
            data-value={item?.value === null ? 'unlimited' : String(item?.value ?? '')}
            sx={{ color: item?.isDefault ? 'text.secondary' : 'text.primary' }}
          >
            <ApprovalLimitValue unit={definition.unit} value={item?.value} />
          </Box>
        );
      },
    })),
    {
      id: 'actions',
      headerKey: 'iam:approval_limits.edit',
      cell: (row) =>
        row.editable ? (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            disabled={!manage.canWrite}
            onClick={() => setEditing(row)}
            data-testid={`limit-edit-${row.code}`}
          >
            {t('iam:approval_limits.edit')}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('iam:approval_limits.locked')}
          </Typography>
        ),
    },
  ];

  return (
    <Stack spacing={2} data-testid="approval-limits">
      <Typography color="text.secondary">{t('iam:approval_limits.subtitle')}</Typography>
      <DataTable
        captionKey="iam:approval_limits.title"
        columns={columns}
        rows={matrix.data?.roles}
        getRowId={(row) => row.roleId}
        isLoading={matrix.isPending}
        error={matrix.error}
        onRetry={() => void matrix.refetch()}
        emptyTitleKey="iam:roles.empty"
      />
      {editing && (
        <LimitsDialog
          row={editing}
          keys={keys}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            onNotice(t('iam:approval_limits.saved'));
          }}
        />
      )}
    </Stack>
  );
}
